import type { InstallationStatus } from "@clubcore/api";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Link } from "react-router";
import { useT } from "../i18n/I18nProvider";
import { Icon } from "../ui/Icon";
import { useCan, useClub } from "./club";
import { useLicense, licenseStateKey } from "./license-context";
import { useApi, useServices } from "./services";

export const INSTALLATION_VIEW = "installation.view";

/** The installation's status for people who may see it; refreshed every few minutes. */
export function useInstallationStatus(enabled = true) {
  const api = useApi();
  const { license } = useServices();
  const { club } = useClub();
  const allowed = useCan()(INSTALLATION_VIEW);
  const query = useQuery({
    queryKey: [club.slug, "installation-status"],
    enabled: enabled && allowed,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    queryFn: ({ signal }) => api.installation.status(signal),
  });
  const state = query.data?.license.status;
  useEffect(() => {
    // The status endpoint is authoritative; let the rest of the app (disabled buttons) follow it.
    if (state && query.data?.enforcement.enabled) license.report(state);
  }, [state, query.data?.enforcement.enabled, license]);
  return query;
}

function describe(status: InstallationStatus, t: ReturnType<typeof useT>): { text: string; tone: "ok" | "warn" | "bad" } {
  const { license } = status;
  if (!status.enforcement.enabled) return { text: t("license.unenforced"), tone: "ok" };
  if (license.status === "valid") return { text: t("license.state.valid"), tone: "ok" };
  if (license.status === "grace") return { text: t("license.indicatorGrace", { count: license.grace_days_remaining ?? 0 }), tone: "warn" };
  return { text: t("license.indicatorReadOnly"), tone: "bad" };
}

/** A small pill in the top bar for people with `installation.view`; links to the status page. */
export function LicenseIndicator() {
  const t = useT();
  const allowed = useCan()(INSTALLATION_VIEW);
  const query = useInstallationStatus();
  if (!allowed || !query.data) return null;
  const { text, tone } = describe(query.data, t);
  return (
    <Link
      to="/admin/installation"
      className={`cc-license cc-license--${tone}`}
      aria-label={`${text}. ${t("license.openStatus")}`}
    >
      <Icon name={tone === "ok" ? "shield" : "alert"} size={16} />
      <span>{text}</span>
    </Link>
  );
}

/** Explains, above every page, why changes are off (or that the grace period is running). */
export function LicenseBanner() {
  const t = useT();
  const { status, readOnly, inGrace } = useLicense();
  if (!status || (!readOnly && !inGrace)) return null;
  const state = t(licenseStateKey(status)).toLowerCase();
  return (
    <div className={readOnly ? "cc-banner cc-banner--bad" : "cc-banner cc-banner--warn"} role="status">
      <Icon name="alert" size={18} />
      <span>{readOnly ? t("license.banner.readOnly", { state }) : t("license.banner.grace")}</span>
    </div>
  );
}
